import { apiFetch } from './client';

export type SmsDirection = 'inbound' | 'outbound';
export type SmsStatus = 'queued' | 'sent' | 'delivered' | 'failed' | 'blocked_dnc';

export interface SmsMessageRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  carrier_id: string | null;
  direction: SmsDirection;
  from_number: string;
  to_number: string;
  body: string | null;
  provider_ref: string | null;
  status: SmsStatus;
  caller_id: string | null;
  broadcast_id: string | null;
  created_at: string;
  sent_at: string | null;
  delivered_at: string | null;
  failed_at: string | null;
  /** Inbound read receipt (migration 0112). NULL = unread. Outbound rows
   *  are never marked and always stay NULL. */
  read_at: string | null;
  /** Why a failed message failed (migration 0117): provider send rejection
   *  or the carrier delivery-receipt error ("code: title"). NULL until a
   *  failure occurs. */
  failure_reason: string | null;
}

export interface SmsDncRow {
  id: string;
  e164: string;
  reason: string | null;
  created_at: string;
}

export interface SendSmsBody {
  from_number: string;
  to_number: string;
  body: string;
  carrier_id?: string | null;
  caller_id?: string | null;
  customer_id?: string;
}

/**
 * Platform-level SMS provider config (super-admin only).
 * The GET view never returns secrets — `has_creds` / `has_public_key`
 * tell the form whether to show a "configured" hint and treat the inputs
 * as write-only (only send a value when the user types a new one).
 */
export interface SmsConfigView {
  configured: boolean;
  provider: 'telnyx' | null;
  messaging_profile_id: string | null;
  default_from: string | null;
  has_creds: boolean;
  has_public_key: boolean;
}

export interface SmsConfigBody {
  provider: 'telnyx';
  /** Omit (or empty) to keep the stored key. Send a value to rotate it. */
  api_key?: string;
  messaging_profile_id: string;
  default_from?: string | null;
  /** Omit (or empty) to keep the stored key. Send a value to rotate it. */
  public_key?: string;
}

export const sms = {
  listMessages: (p: { direction?: SmsDirection; limit?: number; offset?: number; customer_id?: string } = {}) => {
    const q = new URLSearchParams();
    if (p.direction) q.set('direction', p.direction);
    if (p.limit) q.set('limit', String(p.limit));
    if (p.offset) q.set('offset', String(p.offset));
    if (p.customer_id) q.set('customer_id', p.customer_id);
    return apiFetch<{ rows: SmsMessageRow[] }>(`/api/sms-messages?${q}`);
  },
  send: (body: SendSmsBody) =>
    apiFetch<SmsMessageRow>('/api/sms-messages', {
      method: 'POST', body: JSON.stringify(body),
    }),

  /** Count of unread inbound messages (read_at IS NULL) in scope.
   *  customer_id narrows for super_admin / reseller_owner, same as
   *  listMessages. Polled by useSmsUnread for the sidebar badge. */
  unreadCount: (p: { customer_id?: string } = {}) => {
    const q = new URLSearchParams();
    if (p.customer_id) q.set('customer_id', p.customer_id);
    return apiFetch<{ count: number }>(`/api/sms-messages/unread-count?${q}`);
  },
  /** Read receipt: mark one conversation ({ party }) or everything
   *  ({ all: true }) read. Returns how many rows were stamped. */
  markRead: (body: { party?: string; all?: boolean; customer_id?: string }) =>
    apiFetch<{ updated: number }>('/api/sms-messages/mark-read', {
      method: 'POST', body: JSON.stringify(body),
    }),

  listDnc: (p: { search?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (p.search) q.set('search', p.search);
    if (p.limit) q.set('limit', String(p.limit));
    return apiFetch<{ rows: SmsDncRow[] }>(`/api/sms-dnc?${q}`);
  },
  addDnc: (e164: string, reason?: string | null) =>
    apiFetch<{ e164: string }>('/api/sms-dnc', {
      method: 'POST',
      body: JSON.stringify({ e164, reason: reason ?? null }),
    }),
  removeDnc: (e164: string) =>
    apiFetch<void>(`/api/sms-dnc/${encodeURIComponent(e164)}`, { method: 'DELETE' }),
  checkDnc: (e164: string) =>
    apiFetch<{ e164: string; blocked: boolean }>(
      `/api/sms-dnc/check?e164=${encodeURIComponent(e164)}`,
    ),

  /** Super-admin: read the platform Telnyx config (no secrets returned). */
  getConfig: () => apiFetch<SmsConfigView>('/api/platform-settings/sms'),
  /** Super-admin: write the platform Telnyx config. */
  saveConfig: (body: SmsConfigBody) =>
    apiFetch<{ ok: true }>('/api/platform-settings/sms', {
      method: 'PUT', body: JSON.stringify(body),
    }),

  // ── Inbound notification settings (customers.settings, migration 0117) ────

  getNotificationSettings: (p: { customer_id?: string } = {}) => {
    const q = new URLSearchParams();
    if (p.customer_id) q.set('customer_id', p.customer_id);
    return apiFetch<SmsNotificationSettings & { contact_email: string | null }>(
      `/api/sms-notification-settings?${q}`,
    );
  },
  saveNotificationSettings: (body: SmsNotificationSettings & { customer_id?: string }) =>
    apiFetch<SmsNotificationSettings>('/api/sms-notification-settings', {
      method: 'PUT', body: JSON.stringify(body),
    }),
};

export interface SmsNotificationSettings {
  /** Email the account when an inbound SMS arrives. */
  notify_email: boolean;
  /** Override recipient; null = the account's contact email. */
  notify_email_to: string | null;
  /** Forward every inbound SMS to this number (from the receiving DID). */
  forward_e164: string | null;
}
